if (!customElements.get('elaborate-ingredients-modal')) {
  customElements.whenDefined('modal-dialog').then(() => {
    const ModalDialogClass = customElements.get('modal-dialog');

    class ElaborateIngredientsModal extends ModalDialogClass {
      constructor() {
        super();
        this.scrollContainer = this.querySelector('[data-modal-scroll-container]');
        this.scrollbar = this.querySelector('[data-modal-scrollbar]');
        this.thumb = this.querySelector('[data-modal-scrollbar-thumb]');
        this.overlay = this.querySelector('[data-modal-overlay]');
        this.updateScrollbar = this.updateScrollbar.bind(this);
        this.onThumbPointerDown = this.onThumbPointerDown.bind(this);
        this.onThumbPointerMove = this.onThumbPointerMove.bind(this);
        this.onThumbPointerUp = this.onThumbPointerUp.bind(this);
        this.initScrollbar();
        this.overlay?.addEventListener('click', () => this.hide());
      }

      initScrollbar() {
        if (!this.scrollContainer || !this.scrollbar || !this.thumb) return;

        this.scrollContainer.addEventListener('scroll', this.requestScrollbarUpdate.bind(this), {
          passive: true,
        });
        this.thumb.addEventListener('pointerdown', this.onThumbPointerDown);

        this.resizeObserver = new ResizeObserver(this.updateScrollbar);
        this.resizeObserver.observe(this.scrollContainer);
        this.resizeObserver.observe(this.scrollContainer.firstElementChild ?? this.scrollContainer);
      }

      requestScrollbarUpdate() {
        if (this.scrollbarFrame) return;
        this.scrollbarFrame = requestAnimationFrame(() => {
          this.scrollbarFrame = null;
          this.updateScrollbar();
        });
      }

      updateScrollbar() {
        if (!this.scrollContainer || !this.scrollbar || !this.thumb) return;

        const { scrollHeight, clientHeight, scrollTop } = this.scrollContainer;
        const scrollableDistance = scrollHeight - clientHeight;

        if (scrollableDistance <= 1) {
          this.scrollbar.classList.remove('elaborate-ingredients-modal__scrollbar--visible');
          return;
        }

        this.scrollbar.classList.add('elaborate-ingredients-modal__scrollbar--visible');

        const trackHeight = this.scrollbar.clientHeight;
        const thumbHeight = Math.max(
          32,
          (clientHeight / scrollHeight) * trackHeight
        );
        const progress = scrollTop / scrollableDistance;

        this.thumb.style.height = `${thumbHeight}px`;
        this.thumb.style.transform = `translateY(${progress * (trackHeight - thumbHeight)}px)`;
      }

      onThumbPointerDown(event) {
        event.preventDefault();
        this.dragStartY = event.clientY;
        this.dragStartScrollTop = this.scrollContainer.scrollTop;
        this.thumb.setPointerCapture(event.pointerId);
        this.thumb.addEventListener('pointermove', this.onThumbPointerMove);
        this.thumb.addEventListener('pointerup', this.onThumbPointerUp);
        this.thumb.addEventListener('pointercancel', this.onThumbPointerUp);
      }

      onThumbPointerMove(event) {
        const trackHeight = this.scrollbar.clientHeight;
        const thumbHeight = this.thumb.offsetHeight;
        const draggableTrack = trackHeight - thumbHeight;
        if (draggableTrack <= 0) return;

        const scrollableDistance =
          this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight;
        const deltaY = event.clientY - this.dragStartY;

        this.scrollContainer.scrollTop =
          this.dragStartScrollTop + (deltaY / draggableTrack) * scrollableDistance;
      }

      onThumbPointerUp(event) {
        this.thumb.releasePointerCapture(event.pointerId);
        this.thumb.removeEventListener('pointermove', this.onThumbPointerMove);
        this.thumb.removeEventListener('pointerup', this.onThumbPointerUp);
        this.thumb.removeEventListener('pointercancel', this.onThumbPointerUp);
      }

      show(opener) {
        super.show(opener);
        this.scrollToTarget(opener);
        requestAnimationFrame(this.updateScrollbar);
      }

      hide() {
        if (!this.hasAttribute('open') || this.classList.contains('is-closing')) return;

        this.classList.add('is-closing');
        const content = this.querySelector('.elaborate-ingredients-modal__content');

        const finalize = () => {
          this.classList.remove('is-closing');
          super.hide();
        };

        if (!content) {
          finalize();
          return;
        }

        content.addEventListener('transitionend', finalize, { once: true });
        setTimeout(finalize, 400);
      }

      scrollToTarget(opener) {
        if (!this.scrollContainer) return;

        const targetHandle = opener?.dataset.ingredientTarget;
        if (!targetHandle) {
          this.scrollContainer.scrollTop = 0;
          return;
        }

        const target = this.querySelector(`[data-ingredient="${targetHandle}"]`);
        if (!target) return;

        requestAnimationFrame(() => {
          const containerRect = this.scrollContainer.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          this.scrollContainer.scrollTo({
            top: this.scrollContainer.scrollTop + targetRect.top - containerRect.top,
            behavior: 'smooth',
          });
        });
      }
    }

    customElements.define('elaborate-ingredients-modal', ElaborateIngredientsModal);
  });
}
